/**
 * DataBridge — BEMS 데이터 단일 접근 계층
 * ==========================================
 * 모든 컴포넌트/서비스는 BMS 포인트 값을 이 서비스를 통해서만 읽습니다.
 *
 * ┌──────────────────────────────────────────────────────┐
 * │  Dashboard / AI / Reports  (Application Layer)       │
 * │               ↓  only talks to DataBridge            │
 * │  ┌───────────────────────────────────────────────┐   │
 * │  │              DataBridge (this file)           │   │
 * │  │  - 단일 포인트 조회   get(id)                 │   │
 * │  │  - 스냅샷 조회       getSnapshot()            │   │
 * │  │  - 시계열 이력       getHistory(id, since)    │   │
 * │  │  - 실시간 구독       subscribe(id, cb)        │   │
 * │  │  - 소스 전환         setSource(...)           │   │
 * │  └──────┬─────────────────────┬─────────────────┘   │
 * │         │                     │                      │
 * │  SimulationDataSource   HistoricalDataSource         │
 * │  (BMSSimulationService) (HistoricalService)          │
 * │                                                      │
 * │  나중에 Edge 연결 시 MQTTDataSource 추가만 하면 됨   │
 * └──────────────────────────────────────────────────────┘
 *
 * Edge 계층(실제 BACnet/MQTT) 연결 시 교체할 부분:
 *   setSource('live') 호출 → MQTTDataSource가 대신 동작
 *   Application Layer 코드는 수정 없음
 */

import { BMSSimulationService } from './BMSSimulationService';
import { HistoricalService }    from './HistoricalService';
import { LiveBmsService }       from './LiveBmsService';
import { EnergyPlusTestbedService } from './EnergyPlusTestbedService';
import { getPreferredDataSourceMode } from './DataSourcePreference';
import { BuildingConfigService } from './BuildingConfigService';
import { PointRegistryService } from './PointRegistryService';

// ── 타입 정의 ───────────────────────────────────────────────────────────────

export type DataSourceMode = 'simulation' | 'historical' | 'live' | 'energyplus';
export type PointDataSource = 'historical-measured' | 'live-measured' | 'energyplus' | 'simulation' | 'unavailable';

export interface PointReading {
  pointId: string;
  value: number | null;
  source: PointDataSource;
  timestamp: number;
  fallbackUsed: boolean;
}

export interface TimeSeriesEntry {
  ts:  number;   // Unix ms
  val: number;
}

export interface PointSnapshot {
  id:        string;
  value:     number | null;
  timestamp: number;
}

/** 차트/보고서용 KPI 집계 스냅샷 */
export interface KpiSnapshot {
  ts:          number;
  power:       number | null;
  baseline:    number | null;
  temp:        number | null;
  humidity:    number | null;
  outdoorTemp: number | null;
  co2:         number | null;
  pmv:         number | null;
  saving:      number | null;
  rlReward:    number | null;
  quality: {
    power: boolean;
    temp: boolean;
    humidity: boolean;
    outdoorTemp: boolean;
    co2: boolean;
  };
}

// ── 내부 상수 ────────────────────────────────────────────────────────────────

/** 포인트당 보관할 최대 이력 수 (30초 간격 × 2880 = 24시간) */
const HISTORY_MAX = 2880;

/** 이력 기록 간격 (ms) — BMSSimulationService 업데이트 주기와 맞춤 */
const RECORD_INTERVAL_MS = 30_000;

// ── DataBridge 구현 ──────────────────────────────────────────────────────────

class _DataBridge {
  private _mode: DataSourceMode = getPreferredDataSourceMode();

  /** 포인트별 시계열 링버퍼  pointId → entries (오래된 것 앞, 최신 것 뒤) */
  private _history = new Map<string, TimeSeriesEntry[]>();

  /** KPI 집계 이력 (차트/보고서용, 서버와 동기화) */
  private _kpiHistory: KpiSnapshot[] = [];

  /** 포인트별 구독자 */
  private _pointSubs = new Map<string, Set<(val: number) => void>>();

  /** 전체 스냅샷 구독자 */
  private _snapSubs = new Set<(snap: Record<string, number | null>) => void>();

  /** 이력 기록 타이머 */
  private _recordTimer: ReturnType<typeof setInterval> | null = null;

  /** RL 최신 보상값 캐시 */
  private _lastRlReward: number | null = null;
  private _rlPollTimer: ReturnType<typeof setInterval> | null = null;

  // ── 초기화 ─────────────────────────────────────────────────────────────────

  /**
   * DataBridge를 시작합니다.
   * App.tsx에서 BMSSimulationService.start() 호출 직후에 실행하세요.
   */
  async start() {
    if (this._recordTimer) return;
    // 서버에서 기존 이력 로드 (24h)
    await this._fetchServerHistory();
    // 즉시 첫 스냅샷 기록
    this._record();
    this._recordTimer = setInterval(() => this._record(), RECORD_INTERVAL_MS);

    // RL 메트릭 폴링
    this._pollRlMetrics();
    this._rlPollTimer = setInterval(() => this._pollRlMetrics(), 30_000);
  }

  stop() {
    if (this._recordTimer) {
      clearInterval(this._recordTimer);
      this._recordTimer = null;
    }
    if (this._rlPollTimer) {
      clearInterval(this._rlPollTimer);
      this._rlPollTimer = null;
    }
    LiveBmsService.stop();
  }

  // ── 소스 전환 (핵심: 이 메서드만 바꾸면 Edge 교체 완료) ────────────────────

  /**
   * 데이터 소스를 전환합니다.
   *
   * 현재:
   *   'simulation' → BMSSimulationService (기본값)
   *   'historical' → HistoricalService (과거 데이터 재생)
   *
   * 추후 Edge 연결 시 추가:
   *   'live'       → MQTTDataSource (실제 BMS 포인트)
   */
  setSource(mode: DataSourceMode) {
    this._mode = mode;
    if (mode === 'live') LiveBmsService.start();
    if (mode === 'energyplus') EnergyPlusTestbedService.start();
    console.log(`[DataBridge] source → ${mode}`);
  }

  getMode(): DataSourceMode {
    return this._mode;
  }

  // ── 포인트 값 조회 ─────────────────────────────────────────────────────────

  /**
   * 단일 포인트 값 조회.
   * 값이 없으면 fallback(기본 0) 반환.
   */
  get(pointId: string, fallback = 0): number {
    const v = this.getRaw(pointId);
    return v !== null ? v : fallback;
  }

  /**
   * 단일 포인트 값 조회 (null 허용).
   * 값이 없거나 비활성 상태이면 null.
   */
  getReading(pointId: string, options: { allowSimulationFallback?: boolean } = {}): PointReading {
    const allowSimulationFallback = options.allowSimulationFallback ?? this._mode !== 'live';
    const historicalActive = this._mode === 'historical' || HistoricalService.isActive();

    if (historicalActive) {
      const measured = HistoricalService.get(pointId);
      if (measured !== null) {
        const status = HistoricalService.getStatus();
        const date = status.date.length === 8
          ? `${status.date.slice(0, 4)}-${status.date.slice(4, 6)}-${status.date.slice(6, 8)}`
          : status.date;
        const time = new Date(status.seconds * 1000).toISOString().slice(11, 19);
        return {
          pointId,
          value: measured,
          source: 'historical-measured',
          timestamp: new Date(`${date}T${time}+09:00`).getTime(),
          fallbackUsed: false,
        };
      }
      if (!allowSimulationFallback) {
        return {
          pointId,
          value: null,
          source: 'unavailable',
          timestamp: Date.now(),
          fallbackUsed: false,
        };
      }
    }

    const liveActive = this._mode === 'live';
    if (liveActive) {
      LiveBmsService.start();
      const live = LiveBmsService.getReading(pointId);
      if (live) {
        return {
          pointId,
          value: live.value,
          source: 'live-measured',
          timestamp: live.timestamp,
          fallbackUsed: false,
        };
      }
      if (!allowSimulationFallback) {
        return {
          pointId,
          value: null,
          source: 'unavailable',
          timestamp: Date.now(),
          fallbackUsed: false,
        };
      }
    }

    if (this._mode === 'energyplus') {
      EnergyPlusTestbedService.start();
      const reading = EnergyPlusTestbedService.getReading(pointId);
      return reading
        ? {
            pointId,
            value: reading.value,
            source: 'energyplus',
            timestamp: reading.timestamp,
            fallbackUsed: false,
          }
        : {
            pointId,
            value: null,
            source: 'unavailable',
            timestamp: Date.now(),
            fallbackUsed: false,
          };
    }

    if (!allowSimulationFallback) {
      return {
        pointId,
        value: null,
        source: 'unavailable',
        timestamp: Date.now(),
        fallbackUsed: false,
      };
    }

    const simulated = BMSSimulationService.getRaw(pointId);
    return {
      pointId,
      value: simulated,
      source: simulated === null ? 'unavailable' : 'simulation',
      timestamp: Date.now(),
      fallbackUsed: (historicalActive || liveActive) && simulated !== null,
    };
  }

  getMeasuredReading(pointId: string): PointReading {
    return this.getReading(pointId, { allowSimulationFallback: false });
  }

  getRaw(pointId: string): number | null {
    return this.getReading(pointId).value;
    /*
    // Historical 모드가 활성이면 실측값 우선
    if (this._mode === 'historical' || HistoricalService.isActive()) {
      const h = HistoricalService.get(pointId);
      if (h !== null) return h;
    }

    // 'live' 모드 — 추후 MQTTDataSource.get(pointId) 호출로 교체
    // if (this._mode === 'live') return MQTTDataSource.get(pointId);

    // Simulation fallback
    return BMSSimulationService.getRaw(pointId);
    */
  }

  /**
   * 현재 모든 포인트 스냅샷 반환.
   */
  getSnapshot(): Record<string, number | null> {
    if (this._mode === 'energyplus') {
      EnergyPlusTestbedService.start();
      return EnergyPlusTestbedService.getSnapshot();
    }
    if (this._mode === 'historical' || HistoricalService.isActive()) {
      // Historical 값 + simulation으로 채우기
      const sim  = BMSSimulationService.getSnapshot();
      const hist = HistoricalService.getSnapshot();
      return { ...sim, ...hist };
    }
    if (this._mode === 'live') {
      LiveBmsService.start();
      const live = LiveBmsService.getSnapshot();
      return live;
    }
    return BMSSimulationService.getSnapshot();
  }

  // ── 시계열 이력 조회 ────────────────────────────────────────────────────────

  /**
   * 특정 포인트의 시계열 이력을 반환합니다.
   *
   * @param pointId  BMS 포인트 ID
   * @param sinceMs  이 시각(Unix ms) 이후 데이터만. 생략 시 전체.
   * @param limitN   최대 반환 개수. 생략 시 전체.
   */
  getHistory(pointId: string, sinceMs?: number, limitN?: number): TimeSeriesEntry[] {
    let entries = this._history.get(pointId) ?? [];
    if (sinceMs !== undefined) {
      entries = entries.filter(e => e.ts >= sinceMs);
    }
    if (limitN !== undefined && entries.length > limitN) {
      entries = entries.slice(entries.length - limitN);
    }
    return entries;
  }

  /**
   * 여러 포인트의 이력을 한 번에 반환합니다.
   * 차트/보고서용.
   */
  getMultiHistory(
    pointIds: string[],
    sinceMs?: number,
    limitN?: number,
  ): Record<string, TimeSeriesEntry[]> {
    const result: Record<string, TimeSeriesEntry[]> = {};
    for (const id of pointIds) {
      result[id] = this.getHistory(id, sinceMs, limitN);
    }
    return result;
  }

  /**
   * 이력에 저장된 포인트 ID 목록.
   */
  getTrackedPointIds(): string[] {
    return Array.from(this._history.keys());
  }

  /**
   * KPI 집계 이력 반환 (차트/보고서용).
   * 서버 이력 + 현재 세션 이력을 합쳐서 반환.
   *
   * @param range   '24h' | '7d' | '30d'
   * @param maxPts  최대 반환 포인트 수 (다운샘플링)
   */
  getKpiHistory(range: '24h' | '7d' | '30d' = '24h', maxPts = 200): KpiSnapshot[] {
    const rangeMs = range === '30d' ? 30 * 86_400_000
                  : range === '7d'  ?  7 * 86_400_000
                  :                        86_400_000;
    const sinceMs = Date.now() - rangeMs;
    const filtered = this._kpiHistory.filter(e => e.ts >= sinceMs);
    if (filtered.length === 0) return [];
    if (filtered.length <= maxPts) return filtered;
    // 균등 다운샘플링
    const step = filtered.length / maxPts;
    return Array.from({ length: maxPts }, (_, i) => filtered[Math.floor(i * step)]);
  }

  // ── 실시간 구독 ─────────────────────────────────────────────────────────────

  /**
   * 특정 포인트 값 변경 구독.
   * 반환값은 구독 해제 함수입니다.
   *
   * @example
   * const unsub = DataBridge.subscribe('DDC_G.D.D0100', val => setTemp(val));
   * // cleanup:
   * unsub();
   */
  subscribe(pointId: string, cb: (val: number) => void): () => void {
    if (!this._pointSubs.has(pointId)) {
      this._pointSubs.set(pointId, new Set());
    }
    this._pointSubs.get(pointId)!.add(cb);
    return () => this._pointSubs.get(pointId)?.delete(cb);
  }

  /**
   * 전체 스냅샷 구독.
   * RECORD_INTERVAL_MS(30초)마다 콜백 호출.
   */
  subscribeAll(cb: (snap: Record<string, number | null>) => void): () => void {
    this._snapSubs.add(cb);
    return () => this._snapSubs.delete(cb);
  }

  // ── 편의 메서드 ─────────────────────────────────────────────────────────────

  /**
   * 여러 포인트를 한 번에 조회합니다.
   * { pointId: value } 형태로 반환.
   */
  getMultiple(pointIds: string[], fallback = 0): Record<string, number> {
    const result: Record<string, number> = {};
    for (const id of pointIds) {
      result[id] = this.get(id, fallback);
    }
    return result;
  }

  /**
   * 포인트 배열의 평균값을 반환합니다.
   */
  getAverage(pointIds: string[], fallback = 0): number {
    if (pointIds.length === 0) return fallback;
    const vals = pointIds.map(id => this.get(id, fallback));
    return vals.reduce((s, v) => s + v, 0) / vals.length;
  }

  /**
   * 이력에서 특정 포인트의 최근 N분 평균을 반환합니다.
   */
  getRecentAverage(pointId: string, minutes = 30): number | null {
    const sinceMs = Date.now() - minutes * 60 * 1000;
    const entries = this.getHistory(pointId, sinceMs);
    if (entries.length === 0) return null;
    return entries.reduce((s, e) => s + e.val, 0) / entries.length;
  }

  // ── 내부: 서버 이력 로드 + push ──────────────────────────────────────────────

  /** RL 서버에서 최신 보상값을 가져와 캐시한다 */
  private async _pollRlMetrics() {
    try {
      const res = await fetch('/api/rl/metrics');
      if (!res.ok) return;
      const data = await res.json();
      const rewards: number[] = data.episode_rewards ?? [];
      this._lastRlReward = rewards.length > 0 ? rewards[rewards.length - 1] : null;
    } catch { /* RL 서버 오프라인 — 무시 */ }
  }

  /** 서버에서 최근 24시간 KPI 이력을 가져와 _kpiHistory에 채운다 */
  private async _fetchServerHistory() {
    try {
      const res = await fetch('/api/timeseries/query?range=24h&points=2880');
      if (!res.ok) return;
      const json = await res.json();
      if (!Array.isArray(json.data)) return;
      // KPI 이력 버퍼에 저장
      this._kpiHistory = json.data.map((d: any) => ({
        ts:          d.ts,
        power:       d.quality?.power === true && Number.isFinite(d.power) ? d.power : null,
        baseline:    null,
        temp:        d.quality?.temp === true && Number.isFinite(d.temp) ? d.temp : null,
        humidity:    d.quality?.humidity === true && Number.isFinite(d.humidity) ? d.humidity : null,
        outdoorTemp: d.quality?.outdoorTemp === true && Number.isFinite(d.outdoorTemp) ? d.outdoorTemp : null,
        co2:         d.quality?.co2 === true && Number.isFinite(d.co2) ? d.co2 : null,
        pmv:         d.quality?.temp === true && Number.isFinite(d.pmv) ? d.pmv : null,
        saving:      null,
        rlReward:    d.rlReward ?? null,
        quality: {
          power: d.quality?.power === true,
          temp: d.quality?.temp === true,
          humidity: d.quality?.humidity === true,
          outdoorTemp: d.quality?.outdoorTemp === true,
          co2: d.quality?.co2 === true,
        },
      }));
      console.log(`[DataBridge] 서버 이력 로드: ${this._kpiHistory.length}개`);
    } catch { /* 서버 오프라인 — 무시 */ }
  }

  /** 현재 KPI 스냅샷을 서버에 push */
  private _pushToServer(snapshot: KpiSnapshot) {
    fetch('/api/timeseries/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(snapshot),
    }).catch(() => { /* 무시 */ });
  }

  // ── 내부: 이력 기록 ─────────────────────────────────────────────────────────

  private _record() {
    const snap = this.getSnapshot();
    const ts   = Date.now();

    for (const [id, val] of Object.entries(snap)) {
      if (val === null) continue;

      // 링버퍼에 추가
      if (!this._history.has(id)) this._history.set(id, []);
      const buf = this._history.get(id)!;
      buf.push({ ts, val });

      // 최대 크기 초과 시 오래된 것 제거
      if (buf.length > HISTORY_MAX) buf.splice(0, buf.length - HISTORY_MAX);

      // 포인트별 구독자에게 알림
      this._pointSubs.get(id)?.forEach(cb => cb(val));
    }

    // ── KPI 스냅샷 계산 & 서버 push ───────────────────────────────────────
    const kpi = this._buildKpiSnapshot(snap, ts);
    this._kpiHistory.push(kpi);
    if (this._kpiHistory.length > HISTORY_MAX) {
      this._kpiHistory.splice(0, this._kpiHistory.length - HISTORY_MAX);
    }
    this._pushToServer(kpi);

    // 전체 스냅샷 구독자에게 알림
    if (this._snapSubs.size > 0) {
      this._snapSubs.forEach(cb => cb(snap));
    }
  }

  private _buildKpiSnapshot(_snap: Record<string, number | null>, ts: number): KpiSnapshot {
    const measured = (id: string) => this.getMeasuredReading(id).value;
    const config = BuildingConfigService.get();

    // 구역 온도 평균 — canonical registry 우선, 레거시 시설은 기존 설정/SEDC fallback 사용
    const registryZoneTemps = PointRegistryService.resolveMany('zone.air.temperature', {}, config);
    const configuredZoneTemps = Object.values(config.floors ?? {})
      .flatMap(floor => floor.zones.map(zone => zone.tempPointId))
      .filter(Boolean);
    const zoneTempIds = registryZoneTemps.length
      ? registryZoneTemps
      : configuredZoneTemps.length ? [...new Set(configuredZoneTemps)] : [
        'DDC_M.D.D0116','DDC_M.D.D0117','DDC_M.D.D0118',
        'DDC_M.D.D0119','DDC_M.D.D0120','DDC_M.D.D0121',
        'DDC_M.D.D0122','DDC_M.D.D0123',
      ];
    const temps = zoneTempIds.map(measured).filter((v): v is number => v !== null);
    const temp  = temps.length > 0 ? temps.reduce((s, v) => s + v, 0) / temps.length : null;

    // CO2 평균
    const registryCo2 = PointRegistryService.resolveMany('zone.air.co2', {}, config);
    const co2Ids = registryCo2.length ? registryCo2 : config.kpiPoints.co2Sensors?.length
      ? config.kpiPoints.co2Sensors
      : ['DDC_G.D.D0152','DDC_G.D.D0153','DDC_G.D.D0154','DDC_G.D.D0155',
         'DDC_G.D.D0158','DDC_G.D.D0159','DDC_G.D.D0160'];
    const co2Vals = co2Ids.map(measured).filter((v): v is number => v !== null);
    const co2 = co2Vals.length > 0 ? Math.round(co2Vals.reduce((s, v) => s + v, 0) / co2Vals.length) : null;

    // 전력 추정 (HP + AHU + 기본 부하)
    const power = [
      measured('BEMS.HVAC.KW'),
      measured('HVAC.POWER.KW'),
      measured('POWER.HVAC.KW'),
      measured('POWER.WHM.HVAC_KW'),
    ].find((value): value is number => typeof value === 'number' && Number.isFinite(value));
    const humidityPoint = PointRegistryService.resolve(
      'building.indoor_air.relative_humidity', {}, config.kpiPoints.indoorHumidity || 'AHU.D4000', config,
    )!;
    const outdoorTempPoint = PointRegistryService.resolve(
      'site.outdoor_air.temperature', {}, config.kpiPoints.outdoorTemp || 'DDC_G.D.D0100', config,
    )!;
    const humidity = measured(humidityPoint);
    const outdoorTemp = measured(outdoorTempPoint);
    const pmv = temp == null ? null : +((temp - 24.0) * 0.25).toFixed(2);

    return {
      ts,
      power:       power == null ? null : +power.toFixed(3),
      baseline:    null,
      temp:        temp == null ? null : +temp.toFixed(1),
      humidity:    humidity == null ? null : +humidity.toFixed(1),
      outdoorTemp: outdoorTemp == null ? null : +outdoorTemp.toFixed(1),
      co2,
      pmv,
      saving:      null,
      rlReward:    this._lastRlReward,
      quality: {
        power: power != null,
        temp: temp != null,
        humidity: humidity != null,
        outdoorTemp: outdoorTemp != null,
        co2: co2 != null,
      },
    };
  }
}

export const DataBridge = new _DataBridge();
